export class PowerDialerBridge {
  constructor({
    DeviceClass,
    tokenUrl,
    fetchImpl = globalThis.fetch.bind(globalThis),
    csrfToken = null,
    nonceFactory = () => globalThis.crypto.randomUUID(),
    onStatus = () => {},
  }) {
    this.DeviceClass = DeviceClass
    this.tokenUrl = tokenUrl
    this.fetchImpl = fetchImpl
    this.csrfToken = csrfToken
    this.nonceFactory = nonceFactory
    this.onStatus = onStatus
    this.device = null
    this.activeCall = null
    this.initPromise = null
    this.callStarted = false
    this.expectedNonce = null
  }

  async startCall({ url, body }) {
    if (this.callStarted) {
      throw new Error('A Power Dialer call is already in progress')
    }
    if (!body?.append) {
      throw new Error('Power Dialer call data is unavailable')
    }

    const nonce = this.nonceFactory()
    body.append('power_dialer_nonce', nonce)
    this.expectedNonce = nonce

    try {
      await this.ensureRegistered()
      this.callStarted = true
      this.onStatus('Calling customer…', 'pending')
      const response = await this.fetchImpl(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
        credentials: 'same-origin',
        body,
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || 'Could not start the call')
      }

      this.onStatus('Waiting for customer to answer…', 'pending')
      return payload
    } catch (error) {
      this.callStarted = false
      this.expectedNonce = null
      this.onStatus(error.message || 'Could not start the call', 'error')
      throw error
    }
  }

  async ensureRegistered() {
    if (this.device) return this.device
    if (this.initPromise) return this.initPromise

    this.initPromise = (async () => {
      this.onStatus('Connecting browser audio…', 'pending')
      const response = await this.fetchImpl(this.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
        credentials: 'same-origin',
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || !payload.token) {
        throw new Error(payload.error || 'Could not initialize browser audio')
      }

      const device = new this.DeviceClass(payload.token, {
        logLevel: 1,
        codecPreferences: ['opus', 'pcmu'],
      })
      device.on('incoming', (call) => this.acceptIncoming(call))
      device.on('error', (error) => {
        this.onStatus(error?.message || 'Browser audio error', 'error')
      })
      device.on('tokenWillExpire', () => this.refreshToken())

      await device.register()
      this.device = device
      this.onStatus('Browser audio ready', 'ready')
      return device
    })()

    try {
      return await this.initPromise
    } finally {
      this.initPromise = null
    }
  }

  async acceptIncoming(call) {
    const nonce = call.customParameters?.get?.('PowerDialerNonce')
    if (!this.callStarted || !this.expectedNonce || nonce !== this.expectedNonce) {
      this.ignoreIncoming(call)
      return
    }
    if (this.activeCall) {
      this.ignoreIncoming(call)
      return
    }

    this.activeCall = call
    call.on('disconnect', () => this.finishCall('Call ended'))
    call.on('cancel', () => this.finishCall('Call cancelled'))
    call.on('reject', () => this.finishCall('Call rejected'))
    call.on('error', (error) => {
      this.finishCall(error?.message || 'Call error', 'error')
    })

    this.onStatus('Connecting rep audio…', 'pending')
    try {
      await call.accept()
      this.onStatus('Connected', 'ready')
    } catch (error) {
      this.finishCall(error?.message || 'Could not connect rep audio', 'error')
      throw error
    }
  }

  ignoreIncoming(call) {
    if (typeof call.ignore === 'function') {
      call.ignore()
    } else {
      call.reject?.()
    }
  }

  finishCall(message, tone = 'neutral') {
    this.activeCall = null
    this.callStarted = false
    this.expectedNonce = null
    this.onStatus(message, tone)
  }

  async refreshToken() {
    if (!this.device) return

    try {
      const response = await this.fetchImpl(this.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
        credentials: 'same-origin',
      })
      if (!response.ok) return
      const payload = await response.json()
      if (payload.token) this.device.updateToken(payload.token)
    } catch (error) {
      this.onStatus('Could not refresh browser audio', 'error')
    }
  }

  destroy() {
    this.activeCall?.disconnect?.()
    this.device?.destroy?.()
    this.device = null
    this.activeCall = null
    this.callStarted = false
    this.expectedNonce = null
  }
}
